// @ts-check
import { gql, useMutation, useQuery } from '@apollo/client';
import { Trans } from '@lingui/macro';
import React from 'react';
import { Text } from 'theme-ui';
import { Box } from '~/design-system';
import Error from '../../Error';
import ImportOptionsSkeleton from '../ImportOptionsSkeleton';
import MasterListImportOptions from './MasterListImportOptions';

/**
 * Renders the ready to import page with the import statistics
 * The view. The props are passed in from useReady - all the logic resides there.
 * @param {{ setFormikContext: function, onSubmit: function} & ReturnType<useReady>} props
 * @returns
 */
export default function ReadyToImport({
  error,
  importData,
  setFormikContext,
  onSubmit,
  loading,
}) {
  if (loading)
    return (
      <Box>
        <ImportOptionsSkeleton />
      </Box>
    );
  if (error) {
    return (
      <Error>
        <Text>
          <Trans>Sorry, internal error occured.</Trans>
        </Text>
        <Text>
          <Trans>Import can't continue.</Trans>
        </Text>
      </Error>
    );
  }
  const { preview } = importData;
  return (
    <Box>
      <MasterListImportOptions
        setFormikContext={setFormikContext}
        onSubmit={onSubmit}
        statistics={preview}
      />
    </Box>
  );
}

export function useReady({ workspaceId, importId }) {
  const { data, error, loading } = useQuery(QUERY, {
    variables: { workspaceId, importId },
  });
  const [importMasterList] = useMutation(IMPORT_MASTER_LIST);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  return {
    importId,
    ...afterQuery(data),
    error,
    loading,
    isSubmitting,
    onSubmit: async (importOptions) => {
      setIsSubmitting(true);
      const variables = {
        importId,
        importOptions,
      };
      await importMasterList({ variables });
      setIsSubmitting(false);
    },
  };
}

function afterQuery(data) {
  if (!data) return {};
  return { importData: data?.workspace?.dataImport };
}

const QUERY = gql`
  query ProductMasterListReadyPage($workspaceId: ID!, $importId: ID!) {
    workspace(workspaceId: $workspaceId) {
      workspaceId
      dataImport(importId: $importId) {
        importId
        importOptions
        preview {
          deletedCount
          object
          updatedCount
          unchangedCount
          addedCount
        }
      }
    }
  }
`;

ReadyToImport.fragment = gql`
  fragment ReadyToImport on DataImport {
    importId
    importOptions
    preview {
      deletedCount
      object
      updatedCount
      unchangedCount
      addedCount
    }
  }
`;

export const IMPORT_MASTER_LIST = gql`
  mutation finalizeProductMasterListImport(
    $importId: ID!
    $importOptions: ProductMasterListImportInput!
  ) {
    result: finalizeProductMasterListImport(
      input: { importId: $importId, importOptions: $importOptions }
    ) {
      code
      success
      message
    }
  }
`;
